export function calcAnalytics(trades) {
  const closed = trades.filter(t => t.status === 'closed' && t.pnl != null)
  if (closed.length === 0) return null

  const pnls = closed.map(t => t.pnl)
  const totalPnl = pnls.reduce((a, b) => a + b, 0)
  const winners = pnls.filter(p => p > 0)
  const losers = pnls.filter(p => p < 0)
  const winRate = (winners.length / closed.length) * 100

  // Profit Factor
  const grossProfit = winners.reduce((a, b) => a + b, 0)
  const grossLoss = Math.abs(losers.reduce((a, b) => a + b, 0))
  const profitFactor = grossLoss === 0 ? grossProfit : grossProfit / grossLoss

  // Max Drawdown
  let peak = 0, maxDrawdown = 0, cumulative = 0
  for (const pnl of pnls) {
    cumulative += pnl
    if (cumulative > peak) peak = cumulative
    const dd = peak - cumulative
    if (dd > maxDrawdown) maxDrawdown = dd
  }

  // Streaks
  let currentStreak = 0, bestWinStreak = 0, worstLossStreak = 0
  let tempWin = 0, tempLoss = 0
  for (const pnl of pnls) {
    if (pnl > 0) {
      tempWin++; tempLoss = 0
      if (tempWin > bestWinStreak) bestWinStreak = tempWin
    } else {
      tempLoss++; tempWin = 0
      if (tempLoss > worstLossStreak) worstLossStreak = tempLoss
    }
  }
  // Current streak (from most recent trade)
  const lastSign = pnls[pnls.length - 1] > 0 ? 1 : -1
  for (let i = pnls.length - 1; i >= 0; i--) {
    if ((pnls[i] > 0 ? 1 : -1) === lastSign) currentStreak++
    else break
  }

  // Sortino Ratio (annualised, using 0 as target return)
  const avgReturn = totalPnl / closed.length
  const downsideReturns = pnls.filter(p => p < 0)
  const downsideDeviation = downsideReturns.length > 0
    ? Math.sqrt(downsideReturns.reduce((a, b) => a + b * b, 0) / closed.length)
    : 0
  const sortinoRatio = downsideDeviation === 0 ? null : (avgReturn / downsideDeviation) * Math.sqrt(252)

  // Equity curve
  let eq = 0
  const equityCurve = closed.map((t, i) => {
    eq += t.pnl
    return { index: i + 1, equity: parseFloat(eq.toFixed(2)), date: new Date(t.entry_date).toLocaleDateString() }
  })

  // Monthly PnL
  const monthlyMap = {}
  for (const t of closed) {
    const key = new Date(t.entry_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
    monthlyMap[key] = (monthlyMap[key] || 0) + t.pnl
  }
  const monthlyPnl = Object.entries(monthlyMap).map(([month, pnl]) => ({ month, pnl: parseFloat(pnl.toFixed(2)) }))

  // Per asset
  const assetMap = {}
  for (const t of closed) {
    if (!assetMap[t.symbol]) assetMap[t.symbol] = { symbol: t.symbol, trades: 0, pnl: 0, wins: 0 }
    assetMap[t.symbol].trades++
    assetMap[t.symbol].pnl += t.pnl
    if (t.pnl > 0) assetMap[t.symbol].wins++
  }
  const byAsset = Object.values(assetMap)
    .map(a => ({ ...a, pnl: parseFloat(a.pnl.toFixed(2)), winRate: parseFloat(((a.wins / a.trades) * 100).toFixed(1)) }))
    .sort((a, b) => b.pnl - a.pnl)

  return {
    totalPnl: parseFloat(totalPnl.toFixed(2)),
    winRate: parseFloat(winRate.toFixed(1)),
    profitFactor: parseFloat(profitFactor.toFixed(2)),
    maxDrawdown: parseFloat(maxDrawdown.toFixed(2)),
    sortinoRatio: sortinoRatio ? parseFloat(sortinoRatio.toFixed(2)) : null,
    bestWinStreak,
    worstLossStreak,
    currentStreak: lastSign === 1 ? `+${currentStreak}W` : `-${currentStreak}L`,
    totalTrades: closed.length,
    winners: winners.length,
    losers: losers.length,
    avgWin: winners.length ? parseFloat((grossProfit / winners.length).toFixed(2)) : 0,
    avgLoss: losers.length ? parseFloat((grossLoss / losers.length).toFixed(2)) : 0,
    equityCurve,
    monthlyPnl,
    byAsset,
  }
}
